import React from 'react'
import { useMatch } from 'react-router-dom'

import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { getPathString } from '@/factories/create-router-factory'

import * as S from './side-nav.styled'
import { SideNavItem, SideNavProps, SideNavRouteItem } from './side-nav.types'

// Note: lets modified clicks (new tab/window) fall through to the browser's native link handling;
// only a plain left click is handled in-app (dirty-form guard, replace).
const handlePlainClick = (event: React.MouseEvent, action: () => void) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
  event.preventDefault()
  action()
}

const SideNavRouteLink = ({ item }: { item: SideNavRouteItem }) => {
  const path = getPathString(item.to)
  const isActive = !!useMatch({ end: false, path: path.split('?')[0] })

  return (
    <S.NavItem $active={isActive} aria-current={isActive ? 'page' : undefined} to={path}>
      <Icon icon={item.icon} size="1.6rem" />
      {item.label}
    </S.NavItem>
  )
}

export const SideNav = <T extends string>({
  activeTab,
  ariaLabel,
  getHref = () => '',
  groups = [],
  header,
  items,
  onTabChange,
}: SideNavProps<T>) => {
  const visibleItems = items.filter((item) => !item.hidden)
  const ungroupedItems = visibleItems.filter((item) => !item.group)

  const renderItem = (item: SideNavItem<T> | SideNavRouteItem) => {
    if ('to' in item) return <SideNavRouteLink key={getPathString(item.to)} item={item} />

    const content = (
      <>
        <Icon icon={item.icon} size="1.6rem" />
        {item.label}
      </>
    )

    if (item.disabled) {
      return (
        <Tooltip
          key={item.slug}
          disabled={!item.disabledReason}
          placement="right"
          trigger={
            <S.NavItem
              $disabled
              aria-disabled
              $active={false}
              as="span"
              role="link"
              tabIndex={-1}
              to={getHref(item.slug)}
            >
              {content}
            </S.NavItem>
          }
        >
          {item.disabledReason}
        </Tooltip>
      )
    }

    return (
      <S.NavItem
        key={item.slug}
        $active={activeTab === item.slug}
        aria-current={activeTab === item.slug ? 'page' : undefined}
        to={getHref(item.slug)}
        onClick={(event) => handlePlainClick(event, () => onTabChange?.(item.slug))}
      >
        {content}
      </S.NavItem>
    )
  }

  return (
    <S.Nav aria-label={ariaLabel}>
      {header}

      <S.NavList>
        {ungroupedItems.map(renderItem)}

        {groups.map((group) => {
          const groupItems = visibleItems.filter((item) => item.group === group.key)
          if (groupItems.length === 0) return null

          return (
            <React.Fragment key={group.key}>
              <S.GroupHeader>{group.label}</S.GroupHeader>
              {groupItems.map(renderItem)}
            </React.Fragment>
          )
        })}
      </S.NavList>
    </S.Nav>
  )
}
